import { type ClassValue, clsx } from "clsx";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { twMerge } from "tailwind-merge";

import { applyTemplateEInks } from "@/lib/primary-text";

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Devise active de la clinique (GNF par défaut). Fixée au chargement du profil
 * (`/auth/me` -> `active_currency`) et au changement de clinique. La facturation
 * d'une clinique est mono-devise : pas de conversion ni de taux de change.
 */
let activeCurrency = "GNF";

export function setActiveCurrency(code: string | null | undefined): void {
  if (code) activeCurrency = code;
}

export function getActiveCurrency(): string {
  return activeCurrency;
}

/**
 * Pays actif de la clinique (code ISO, GN par défaut). Fixé au chargement du
 * profil (`/auth/me` -> `active_country`) et au changement de clinique. Pilote la
 * validation/format des téléphones (cf. lib/phone.ts).
 */
let activeCountry = "GN";

export function setActiveCountry(code: string | null | undefined): void {
  if (code) activeCountry = code;
}

export function getActiveCountry(): string {
  return activeCountry;
}

/**
 * Couleur primaire de la clinique active (hex). Appliquée au runtime en surchargeant
 * la variable CSS `--primary` (canaux RGB), ce qui repeint tous les `bg/text/border-primary`.
 */
let activePrimaryColor = "#0F766E";

function hexToRgbChannels(hex: string): string | null {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m?.[1]) return null;
  const int = Number.parseInt(m[1], 16);
  return `${(int >> 16) & 255} ${(int >> 8) & 255} ${int & 255}`;
}

export function setActivePrimaryColor(hex: string | null | undefined): void {
  if (!hex) return;
  const channels = hexToRgbChannels(hex);
  if (!channels) return;
  activePrimaryColor = hex;
  document.documentElement.style.setProperty("--primary", channels);
  // Encres lisibles de la présentation E (lues sous `[data-ui="e"]` seulement).
  applyTemplateEInks(hex);
}

export function getActivePrimaryColor(): string {
  return activePrimaryColor;
}

/**
 * Confidentialité des montants (masquage global) : état en mémoire UNIQUEMENT
 * (jamais persisté en storage) — masqué par défaut à chaque chargement de
 * session. Basculé par `AmountsVisibilityToggle` (TopNavbar), réservé aux
 * titulaires de la permission `finance.view_amounts` après re-saisie du mot
 * de passe (`POST /auth/verify-amounts/`).
 */
let amountsVisible = false;
const MASKED_AMOUNT = "••••••";

export function setAmountsVisible(visible: boolean): void {
  amountsVisible = visible;
}

export function areAmountsVisible(): boolean {
  return amountsVisible;
}

function formatMoneyValue(
  amount: number | string | null | undefined,
  currency?: string,
): string {
  // Robuste aux valeurs nulles (ex. montant reçu d'un dossier non encore réglé) :
  // évite un crash `null.toLocaleString` qui faisait planter des tables entières.
  const n = amount == null || amount === "" ? 0 : Number(amount);
  const safe = Number.isFinite(n) ? n : 0;
  const code = currency ?? activeCurrency;
  // Convention projet (README) : séparateur de milliers = ESPACE, format
  // « 1 250 000 GNF » — locale fr-FR, alignée sur l'app mobile. (Remplace
  // l'ancien format legacy à points de-DE, contraire à la convention.)
  return `${safe.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} ${code}`;
}

/**
 * Formate un montant dans la devise active (ou `currency` si fournie).
 * Toujours lisible — le masquage confidentialité ne concerne que les tuiles
 * KPI / tableaux de bord via `formatMoneyMasked`.
 */
export function formatMoney(
  amount: number | string | null | undefined,
  currency?: string,
): string {
  return formatMoneyValue(amount, currency);
}

/**
 * Montant SANS code devise (nombre seul, séparateur d'espace fr-FR).
 * Pour les tableaux où la devise est indiquée une seule fois en en-tête —
 * évite de répéter « GNF » dans chaque cellule.
 */
export function formatAmount(
  amount: number | string | null | undefined,
): string {
  const n = amount == null || amount === "" ? 0 : Number(amount);
  const safe = Number.isFinite(n) ? n : 0;
  return safe.toLocaleString("fr-FR", { maximumFractionDigits: 0 });
}

/**
 * Nombre entier (effectif, décompte) au format fr-FR : « 11 489 », pas
 * « 11489 ». Les montants passaient déjà par `formatMoney`, mais les
 * compteurs étaient convertis par un `String(n)` brut — au-delà du millier,
 * ils devenaient illisibles.
 */
export function formatCount(value: number | string | null | undefined): string {
  const n = value == null || value === "" ? 0 : Number(value);
  return (Number.isFinite(n) ? n : 0).toLocaleString("fr-FR", {
    maximumFractionDigits: 0,
  });
}

/**
 * Variante TOUJOURS lisible, réservée aux documents imprimés/PDF (jamais
 * masquée par la confidentialité des montants de l'interface).
 */
export function formatMoneyAlways(
  amount: number | string | null | undefined,
  currency?: string,
): string {
  return formatMoneyValue(amount, currency);
}

export function formatDateFr(value: string | Date): string {
  const d = typeof value === "string" ? new Date(value) : value;
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function formatDateTimeFr(value: string | Date): string {
  const d = typeof value === "string" ? new Date(value) : value;
  return d.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Valeur d'un `<input type="date">` (YYYY-MM-DD) à partir d'une date locale. */
export function toDateInputValue(value: string | Date): string {
  const d = typeof value === "string" ? new Date(value) : value;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Date saisie (YYYY-MM-DD) → instant ISO 8601 envoyé à l'API.
 *
 * Les actes médicaux (consultation, ordonnance) se saisissent au jour près :
 * l'heure n'est pas demandée à l'utilisateur, elle est **horodatée au moment de
 * l'enregistrement**. Une date antérieure conserve donc l'heure courante.
 */
export function dateInputToInstant(value: string, now: Date = new Date()): string {
  // Le préfixe suffit : tolère aussi un ancien brouillon `datetime-local`
  // (YYYY-MM-DDTHH:mm) restauré depuis sessionStorage après une mise à jour.
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return now.toISOString();
  const [, year, month, day] = match.map(Number);
  if (!year || !month || !day) return now.toISOString();
  const stamped = new Date(now);
  stamped.setFullYear(year, month - 1, day);
  return stamped.toISOString();
}

/** Ancienneté relative en français (« il y a 3 mois », « il y a 2 ans »). */
export function formatRelativeFr(value: string | Date): string {
  const d = typeof value === "string" ? new Date(value) : value;
  return formatDistanceToNow(d, { addSuffix: true, locale: fr });
}

/** Libellé court d'une clinique pour les colonnes de tables : le nom complet
 *  s'il tient (≤ 22 caractères, ex. « Espoir Pandjé Kaloum »), sinon son
 *  dernier mot (« Kaloum », « Kamsar », « Lambanyi ») — le nom complet reste
 *  dans l'info-bulle de la cellule. */
export function clinicShortLabel(name?: string | null, max = 22): string {
  const full = (name ?? "").trim();
  if (!full) return "—";
  if (full.length <= max) return full;
  const last = full.split(/\s+/).at(-1) ?? full;
  return last.charAt(0).toUpperCase() + last.slice(1);
}

/** Variante CONFIDENTIELLE de formatMoney pour les tableaux de bord et tuiles
 *  KPI (accueil, résumés des pages Factures/Paiements/Caisse/Rapports) :
 *  retourne « •••••• » tant que les montants ne sont pas déverrouillés.
 *  Les tables de données, elles, restent lisibles (formatMoney). */
export function formatMoneyMasked(
  amount: number | string | null | undefined,
  currency?: string,
): string {
  if (!amountsVisible) return MASKED_AMOUNT;
  return formatMoney(amount, currency);
}
